import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { KeyRound, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Helix } from "@/components/helix";
import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — Understand Your Genome" },
      {
        name: "description",
        content:
          "Sign in with Google or your email so you can come back to your results on any device.",
      },
      { property: "og:title", content: "Sign in — Understand Your Genome" },
      {
        property: "og:description",
        content: "Sign in with Google, a password, or a one-time email link.",
      },
    ],
  }),
  component: AuthPage,
});

export const emailSchema = z.string().trim().email("Please enter a valid email address").max(255);
export const passwordSchema = z.string().min(6, "Password needs at least 6 characters").max(128);

type Notice = { kind: "magic-link" | "confirm-signup" | "password-reset"; email: string } | null;

function AuthPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"sign-in" | "sign-up">("sign-in");
  const [sending, setSending] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [resetMode, setResetMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();

  async function handleGoogleSignIn() {
    setGoogleLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) {
      setGoogleLoading(false);
      toast.error(error.message ?? "Google sign-in failed. Please try email instead.");
      return;
    }
    // browser is navigating to Google
  }

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("mode") === "reset") {
      setResetMode(true);
    }
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/dashboard" });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setResetMode(true);
        return;
      }
      if (event === "SIGNED_IN" && session) navigate({ to: "/dashboard" });
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);

  async function handleForgotPassword() {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check your email address");
      return;
    }
    setSending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(parsed.data, {
      redirectTo: `${window.location.origin}/auth?mode=reset`,
    });
    setSending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setNotice({ kind: "password-reset", email: parsed.data });
  }

  async function handleResetSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = passwordSchema.safeParse(newPassword);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check your password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("The two passwords don't match.");
      return;
    }
    setSending(true);
    const { error } = await supabase.auth.updateUser({ password: parsed.data });
    setSending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Your password has been updated.");
    navigate({ to: "/dashboard" });
  }

  async function handlePasswordSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsedEmail = emailSchema.safeParse(email);
    if (!parsedEmail.success) {
      toast.error(parsedEmail.error.issues[0]?.message ?? "Please check your email address");
      return;
    }
    const parsedPassword = passwordSchema.safeParse(password);
    if (!parsedPassword.success) {
      toast.error(parsedPassword.error.issues[0]?.message ?? "Please check your password");
      return;
    }

    setSending(true);
    if (mode === "sign-in") {
      const { error } = await supabase.auth.signInWithPassword({
        email: parsedEmail.data,
        password: parsedPassword.data,
      });
      setSending(false);
      if (error) {
        toast.error(
          error.message === "Invalid login credentials"
            ? "That email and password don't match an account. New here? Switch to “Create account”."
            : error.message,
        );
        return;
      }
      // onAuthStateChange navigates to /dashboard
    } else {
      const { data, error } = await supabase.auth.signUp({
        email: parsedEmail.data,
        password: parsedPassword.data,
        options: { emailRedirectTo: window.location.origin },
      });
      setSending(false);
      if (error) {
        toast.error(error.message);
        return;
      }
      if (!data.session) {
        setNotice({ kind: "confirm-signup", email: parsedEmail.data });
      }
      // if a session exists, onAuthStateChange navigates to /dashboard
    }
  }

  async function handleMagicLinkSubmit(event: React.FormEvent) {
    event.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please check your email address");
      return;
    }

    setSending(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: parsed.data,
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    });
    setSending(false);

    if (error) {
      toast.error(error.message);
      return;
    }
    setNotice({ kind: "magic-link", email: parsed.data });
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 px-5 py-16">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <div className="relative hidden md:block">
            <Helix className="mx-auto h-80 w-auto" />
          </div>

          <div className="rounded-3xl border border-border bg-card p-8 shadow-sm">
            {notice ? (
              <div className="text-center">
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail className="size-6" />
                </div>
                <h1 className="mt-5 text-2xl">Check your email</h1>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {notice.kind === "magic-link" ? (
                    <>
                      We sent a one-time sign-in link to{" "}
                      <span className="font-medium text-foreground">{notice.email}</span>. Open it
                      on any device and you'll land straight in your account. The link works once
                      and then expires.
                    </>
                  ) : notice.kind === "password-reset" ? (
                    <>
                      We sent a password reset link to{" "}
                      <span className="font-medium text-foreground">{notice.email}</span>. Open it
                      and you'll land back here to choose a new password. The link works once and
                      then expires.
                    </>
                  ) : (
                    <>
                      We sent a confirmation link to{" "}
                      <span className="font-medium text-foreground">{notice.email}</span>. Click it
                      to activate your account, then come back here and sign in.
                    </>
                  )}
                </p>
                <Button variant="ghost" className="mt-6" onClick={() => setNotice(null)}>
                  Back to sign in
                </Button>
              </div>
            ) : resetMode ? (
              <>
                <h1 className="text-2xl">Choose a new password</h1>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Pick a new password for your account. You'll stay signed in afterwards.
                </p>
                <form onSubmit={handleResetSubmit} className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="At least 6 characters"
                      maxLength={128}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm new password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Repeat it"
                      maxLength={128}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    <KeyRound className="size-4" />
                    {sending ? "One moment…" : "Set new password"}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <h1 className="text-2xl">Sign in or create your account</h1>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Use your Google account, an email and password, or a one-time email link.
                </p>

                <Button
                  type="button"
                  variant="outline"
                  className="mt-6 w-full"
                  disabled={googleLoading}
                  onClick={handleGoogleSignIn}
                >
                  <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true">
                    <path
                      fill="#4285F4"
                      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"
                    />
                  </svg>
                  {googleLoading ? "Opening Google…" : "Continue with Google"}
                </Button>

                <div className="mt-5 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  or with your email
                  <span className="h-px flex-1 bg-border" />
                </div>

                <Tabs defaultValue="password" className="mt-5">
                  <TabsList className="w-full">
                    <TabsTrigger value="password" className="flex-1">
                      Password
                    </TabsTrigger>
                    <TabsTrigger value="link" className="flex-1">
                      Email link
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="password">
                    <form onSubmit={handlePasswordSubmit} className="mt-4 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email address</Label>
                        <Input
                          id="email"
                          type="email"
                          autoComplete="email"
                          placeholder="you@example.com"
                          maxLength={255}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
                          placeholder="At least 6 characters"
                          maxLength={128}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                      {mode === "sign-in" && (
                        <div className="text-right">
                          <button
                            type="button"
                            disabled={sending}
                            className="text-sm text-primary underline-offset-4 hover:underline"
                            onClick={handleForgotPassword}
                          >
                            Forgot password?
                          </button>
                        </div>
                      )}
                      <Button
                        type="submit"
                        disabled={sending}
                        className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                      >
                        <KeyRound className="size-4" />
                        {sending
                          ? "One moment…"
                          : mode === "sign-in"
                            ? "Sign in"
                            : "Create account"}
                      </Button>
                      <p className="text-center text-sm text-muted-foreground">
                        {mode === "sign-in" ? (
                          <>
                            New here?{" "}
                            <button
                              type="button"
                              className="font-medium text-primary underline-offset-4 hover:underline"
                              onClick={() => setMode("sign-up")}
                            >
                              Create an account
                            </button>
                          </>
                        ) : (
                          <>
                            Already have an account?{" "}
                            <button
                              type="button"
                              className="font-medium text-primary underline-offset-4 hover:underline"
                              onClick={() => setMode("sign-in")}
                            >
                              Sign in
                            </button>
                          </>
                        )}
                      </p>
                    </form>
                  </TabsContent>

                  <TabsContent value="link">
                    <form onSubmit={handleMagicLinkSubmit} className="mt-4 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="link-email">Email address</Label>
                        <Input
                          id="link-email"
                          type="email"
                          autoComplete="email"
                          placeholder="you@example.com"
                          maxLength={255}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={sending}
                        className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                      >
                        {sending ? "Sending your link…" : "Email me a sign-in link"}
                      </Button>
                      <p className="text-center text-sm text-muted-foreground">
                        No password needed — the link signs you in directly.
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>

                <div className="mt-6 flex gap-3 rounded-2xl bg-secondary/60 p-4 text-sm leading-relaxed text-muted-foreground">
                  <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" />
                  <p>
                    <span className="font-medium text-foreground">Why we ask for an email:</span>{" "}
                    analysing a genome file can take hours, so we email you the moment your
                    annotated file is ready — and your account lets you come back to it later, on
                    any device. We don't send marketing.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
